import { ArrowLeft, Share2, Truck, Zap, Leaf, TrendingUp, AlertTriangle, CheckCircle2, Clock, Calendar, User, Phone, Mail, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useEffect } from 'react';

export default function FutureOfHGVDriving() {
  const shareUrl = window.location.href
  const shareTitle = "The Future of HGV Driving: Electric Trucks and Green Logistics"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>8th June 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Industry News</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              The Future of HGV Driving: <span className="text-c9-orange">Electric Trucks</span> and Green Logistics
            </h1>
            
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              The transport industry is undergoing its biggest transformation in decades, with the government investing £200 million in zero-emission truck technology and new regulations coming into force. Professional drivers need to understand what's ahead.
            </p>
            
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 bg-c9-orange hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <Share2 className="h-5 w-5" />
              Share Article
            </button>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section-padding">
        <div className="container-full">
          <div className="article-content">
            
            <div className="article-section">
              <h2><Zap className="h-8 w-8 text-c9-orange" />Government Leads the Charge with £200 Million Investment</h2>
              <p>The UK government has committed a substantial £200 million to accelerate the development and deployment of zero-emission truck technology and new regulations. This investment represents the largest government commitment to electric freight vehicles in the country's history.</p>
              
              <div className="highlight-box">
                <h3><TrendingUp className="h-6 w-6 text-c9-blue" />Key Investment Areas</h3>
                <ul className="icon-list">
                  <li><CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /><strong>Charging Infrastructure:</strong> £100 million for rapid charging networks along major freight routes</li>
                  <li><CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /><strong>Vehicle Incentives:</strong> £75 million in purchase grants for commercial operators</li>
                  <li><CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /><strong>Training Programmes:</strong> £25 million for driver education and certification</li>
                </ul>
              </div>
            </div>

            <hr className="article-divider" />

            <div className="article-section">
              <h2><Truck className="h-8 w-8 text-c9-orange" />New Emission Vehicle Zones and Compliance</h2>
              <p>The introduction of the Zero Emission Vehicle (ZEV) mandate sets clear targets for manufacturers to ensure a significant proportion of their truck sales are zero-emission vehicles. This progressive framework spans multiple years, with increasingly ambitious targets.</p>
              
              <div className="warning-box">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 mb-2">Important Compliance Deadlines</h4>
                  <p className="text-amber-700">From 2025 onwards, new regulations will require commercial vehicle operators to demonstrate emissions compliance for access to city centres and specific freight corridors.</p>
                </div>
              </div>
            </div>

            <hr className="article-divider" />

            <div className="article-section">
              <h2><Leaf className="h-8 w-8 text-c9-orange" />Opportunities for Professional Drivers</h2>
              <p>The transition to electric vehicles represents a wealth of opportunities for forward-thinking professional drivers. Those who adapt early will find themselves in high demand as the industry transforms.</p>
              
              <h3>New Opportunities Include:</h3>
              <ul className="icon-list">
                <li><Truck className="h-5 w-5 text-c9-blue flex-shrink-0 mt-1" /><strong>Specialist Electric Vehicle Operations:</strong> Operating advanced electric truck fleets with premium pay rates</li>
                <li><Zap className="h-5 w-5 text-c9-blue flex-shrink-0 mt-1" /><strong>Charging Infrastructure Expertise:</strong> Understanding optimal charging strategies and route planning</li>
                <li><TrendingUp className="h-5 w-5 text-c9-blue flex-shrink-0 mt-1" /><strong>Green Logistics Coordination:</strong> Managing sustainable supply chain operations</li>
                <li><CheckCircle2 className="h-5 w-5 text-c9-blue flex-shrink-0 mt-1" /><strong>Training and Mentorship Roles:</strong> Training other drivers in electric vehicle operation</li>
              </ul>
              
              <div className="success-box">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-800 mb-2">Salary Expectations</h4>
                  <p className="text-green-700">Early adopters of electric vehicle technology are seeing salary premiums of 15-25% above traditional HGV rates, with experienced electric truck operators earning £35,000-£45,000 annually.</p>
                </div>
              </div>
            </div>

            <hr className="article-divider" />

            <div className="article-section">
              <h2><Clock className="h-8 w-8 text-c9-orange" />Timeline and Implementation</h2>
              <p>The transition won't happen overnight, but the timeline is becoming clearer with each government announcement and industry commitment.</p>
              
              <div className="highlight-box">
                <h3>Key Milestones:</h3>
                <ul>
                  <li><strong>2025-2026:</strong> Early adopter programmes and infrastructure rollout begins</li>
                  <li><strong>2027-2028:</strong> Mainstream adoption across major freight operators</li>
                  <li><strong>2029-2030:</strong> Full regulatory compliance requirements in effect</li>
                  <li><strong>2031+:</strong> Electric-first transport industry established</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <Zap className="h-6 w-6" />
                Ready to Drive the Future?
              </h3>
              <p className="text-lg mb-6">
                Stay ahead in the evolving transport industry. Contact C9 Recruitment to explore exciting opportunities with forward-thinking companies investing in electric vehicle technology.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  View Green Transport Jobs
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Call Our Team
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Speak with our transport industry specialists about electric vehicle opportunities</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
